/**
 * Sample playlists for the signed-in screen.
 * Replace this module when the playlists API is connected.
 * The screen already swaps in skeletons while `loading` is true.
 */

export type PlaylistTone = 'purple' | 'blue' | 'green' | 'orange' | 'pink';

export type PlaylistIcon =
  | 'list'
  | 'landmark'
  | 'route'
  | 'train'
  | 'run'
  | 'utensils'
  | 'car'
  | 'briefcase'
  | 'plane'
  | 'trees'
  | 'moon';

export type StopColor = 'purple' | 'blue' | 'green' | 'orange' | 'red';

export interface PlaylistStop {
  id: string;
  name: string;
  area: string;
  latitude: number;
  longitude: number;
  waitMinutes: number;
  color: StopColor;
}

export interface PlaylistActivity {
  id: string;
  title: string;
  time: string;
}

export interface Playlist {
  id: string;
  name: string;
  icon: PlaylistIcon;
  tone: PlaylistTone;
  favourite: boolean;
  active: boolean;
  createdLabel: string;
  lastUsedHours: number;
  lastUsedLabel: string;
  lastUsedExact: string;
  timesUsed: number;
  distanceKm: number;
  estimatedMinutes: number;
  collectionId: string;
  mockMinutes: number;
  avgSessionMinutes: number;
  mockDelta: number;
  avgDelta: number;
  usage: number[];
  activity: PlaylistActivity[];
  stops: PlaylistStop[];
}

export interface PlaylistCollection {
  id: string;
  name: string;
}

export const MOST_USED_AT = 5;

export const STOP_COLOR: Record<StopColor, string> = {
  purple: '#7c5cff',
  blue: '#3b82f6',
  green: '#22c55e',
  orange: '#f97316',
  red: '#ef4444',
};

const STOP_CYCLE: StopColor[] = ['purple', 'blue', 'green', 'orange', 'red'];

export const PLAYLIST_COLLECTIONS: PlaylistCollection[] = [
  { id: 'city', name: 'City tours' },
  { id: 'monuments', name: 'Monuments' },
  { id: 'noida', name: 'Noida' },
  { id: 'commute', name: 'Commute' },
  { id: 'weekend', name: 'Weekend' },
  { id: 'food', name: 'Food & cafes' },
  { id: 'airport', name: 'Airport' },
];

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const TREND_LABELS: string[] = Array.from({ length: 31 }, (_, index) => {
  const date = new Date(2026, 8, 22 + index);
  return `${date.getDate()} ${MONTHS[date.getMonth()]}`;
});

const DELHI_USAGE = [2, 3, 2, 4, 3, 5, 2, 3, 4, 3, 2, 5, 4, 3, 2, 4, 5, 3, 2, 4, 3, 5, 4, 2, 3, 6, 4, 3, 5, 4, 7];

let idSeq = 1;

function nextId(prefix: string): string {
  idSeq += 1;
  return `${prefix}-${Date.now().toString(36)}-${idSeq}`;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

type StopRow = [string, string, number];

function ringStops(playlistId: string, center: [number, number], rows: StopRow[]): PlaylistStop[] {
  return rows.map(([name, area, waitMinutes], index) => {
    const angle = (index / Math.max(rows.length, 1)) * Math.PI * 2 - Math.PI / 2;
    const radius = 0.012 + index * 0.006;
    return {
      id: `${playlistId}-${index + 1}`,
      name,
      area,
      waitMinutes,
      latitude: Number(clamp(center[0] + Math.sin(angle) * radius, 28.42, 28.78).toFixed(4)),
      longitude: Number(clamp(center[1] + Math.cos(angle) * radius, 76.96, 77.55).toFixed(4)),
      color: STOP_CYCLE[index % STOP_CYCLE.length],
    };
  });
}

function series(seed: number): number[] {
  return Array.from({ length: 31 }, (_, day) => {
    const wave = 1 + ((seed * 5 + day * 3) % 6);
    const spike = day % 9 === seed % 9 ? 2 : 0;
    return Math.min(8, wave + spike);
  });
}

interface PlaylistDraft {
  id: string;
  name: string;
  icon: PlaylistIcon;
  tone: PlaylistTone;
  favourite?: boolean;
  active?: boolean;
  createdLabel: string;
  lastUsedHours: number;
  lastUsedLabel: string;
  lastUsedExact: string;
  timesUsed: number;
  distanceKm: number;
  estimatedMinutes: number;
  collectionId: string;
  mockMinutes: number;
  avgSessionMinutes: number;
  mockDelta: number;
  avgDelta: number;
  usage?: number[];
  stops: PlaylistStop[];
}

function makePlaylist(draft: PlaylistDraft): Playlist {
  const favourite = draft.favourite === true;
  return {
    id: draft.id,
    name: draft.name,
    icon: draft.icon,
    tone: draft.tone,
    favourite,
    active: draft.active === true,
    createdLabel: draft.createdLabel,
    lastUsedHours: draft.lastUsedHours,
    lastUsedLabel: draft.lastUsedLabel,
    lastUsedExact: draft.lastUsedExact,
    timesUsed: draft.timesUsed,
    distanceKm: draft.distanceKm,
    estimatedMinutes: draft.estimatedMinutes,
    collectionId: draft.collectionId,
    mockMinutes: draft.mockMinutes,
    avgSessionMinutes: draft.avgSessionMinutes,
    mockDelta: draft.mockDelta,
    avgDelta: draft.avgDelta,
    usage: draft.usage ?? series(draft.name.length),
    activity: [
      { id: `${draft.id}-play`, title: 'Playlist played', time: draft.lastUsedExact },
      { id: `${draft.id}-order`, title: 'Stop order updated', time: 'Yesterday, 11:02 AM' },
      {
        id: `${draft.id}-saved`,
        title: favourite ? 'Added to favourites' : 'Playlist created',
        time: draft.createdLabel,
      },
    ],
    stops: draft.stops,
  };
}

const DELHI_STOPS: PlaylistStop[] = [
  { id: 'delhi-1', name: 'India Gate', area: 'New Delhi, Delhi, India', latitude: 28.6129, longitude: 77.2295, waitMinutes: 0, color: 'purple' },
  { id: 'delhi-2', name: "Humayun's Tomb", area: 'New Delhi, Delhi, India', latitude: 28.5933, longitude: 77.2507, waitMinutes: 10, color: 'blue' },
  { id: 'delhi-3', name: 'Lodhi Garden', area: 'New Delhi, Delhi, India', latitude: 28.5931, longitude: 77.2197, waitMinutes: 10, color: 'green' },
  { id: 'delhi-4', name: 'Hauz Khas Village', area: 'New Delhi, Delhi, India', latitude: 28.5494, longitude: 77.2001, waitMinutes: 10, color: 'orange' },
  { id: 'delhi-5', name: 'Qutub Minar', area: 'New Delhi, Delhi, India', latitude: 28.5245, longitude: 77.1855, waitMinutes: 15, color: 'red' },
  { id: 'delhi-6', name: 'Akshardham Temple', area: 'New Delhi, Delhi, India', latitude: 28.6127, longitude: 77.2773, waitMinutes: 10, color: 'purple' },
  { id: 'delhi-7', name: 'Noida Sector 18', area: 'Noida, Uttar Pradesh, India', latitude: 28.5708, longitude: 77.3219, waitMinutes: 10, color: 'red' },
  { id: 'delhi-8', name: 'DLF Mall of India', area: 'Noida, Uttar Pradesh, India', latitude: 28.5672, longitude: 77.3211, waitMinutes: 10, color: 'purple' },
];

export const PLAYLISTS: Playlist[] = [
  makePlaylist({
    id: 'delhi-city',
    name: 'Delhi City Tour',
    icon: 'list',
    tone: 'purple',
    favourite: true,
    active: true,
    createdLabel: '12 Aug 2026',
    lastUsedHours: 2,
    lastUsedLabel: '2 hours ago',
    lastUsedExact: '20 Oct 2026, 4:15 PM',
    timesUsed: 5,
    distanceKm: 42.5,
    estimatedMinutes: 80,
    collectionId: 'city',
    mockMinutes: 400,
    avgSessionMinutes: 80,
    mockDelta: 18,
    avgDelta: 12,
    usage: DELHI_USAGE,
    stops: DELHI_STOPS,
  }),
  makePlaylist({
    id: 'monuments',
    name: 'Monuments Loop',
    icon: 'landmark',
    tone: 'blue',
    createdLabel: '2 Jun 2026',
    lastUsedHours: 24,
    lastUsedLabel: '1 day ago',
    lastUsedExact: '23 Oct 2026, 6:00 PM',
    timesUsed: 3,
    distanceKm: 28.3,
    estimatedMinutes: 65,
    collectionId: 'monuments',
    mockMinutes: 195,
    avgSessionMinutes: 65,
    mockDelta: 6,
    avgDelta: 4,
    stops: ringStops('monuments', [28.656, 77.241], [
      ['Red Fort', 'Old Delhi, Delhi, India', 10],
      ['Jama Masjid', 'Old Delhi, Delhi, India', 15],
      ['Raj Ghat', 'New Delhi, Delhi, India', 5],
      ['India Gate', 'New Delhi, Delhi, India', 0],
      ['National Museum', 'New Delhi, Delhi, India', 20],
      ['Jantar Mantar', 'New Delhi, Delhi, India', 10],
    ]),
  }),
  makePlaylist({
    id: 'noida',
    name: 'Noida Tour',
    icon: 'route',
    tone: 'green',
    favourite: true,
    createdLabel: '18 Jul 2026',
    lastUsedHours: 72,
    lastUsedLabel: '3 days ago',
    lastUsedExact: '21 Oct 2026, 5:10 PM',
    timesUsed: 8,
    distanceKm: 35.1,
    estimatedMinutes: 70,
    collectionId: 'noida',
    mockMinutes: 560,
    avgSessionMinutes: 70,
    mockDelta: 12,
    avgDelta: 8,
    stops: ringStops('noida', [28.57, 77.35], [
      ['Sector 18', 'Noida, Uttar Pradesh, India', 0],
      ['DLF Mall of India', 'Noida, Uttar Pradesh, India', 10],
      ['Botanical Garden', 'Noida, Uttar Pradesh, India', 5],
      ['Film City', 'Noida, Uttar Pradesh, India', 10],
      ['Sector 62', 'Noida, Uttar Pradesh, India', 0],
      ['Worlds of Wonder', 'Noida, Uttar Pradesh, India', 20],
      ['Okhla Bird Sanctuary', 'Noida, Uttar Pradesh, India', 15],
    ]),
  }),
  makePlaylist({
    id: 'metro',
    name: 'Metro Route',
    icon: 'train',
    tone: 'orange',
    createdLabel: '9 May 2026',
    lastUsedHours: 120,
    lastUsedLabel: '5 days ago',
    lastUsedExact: '19 Oct 2026, 9:40 AM',
    timesUsed: 4,
    distanceKm: 65.4,
    estimatedMinutes: 75,
    collectionId: 'commute',
    mockMinutes: 300,
    avgSessionMinutes: 75,
    mockDelta: 4,
    avgDelta: 3,
    stops: ringStops('metro', [28.62, 77.21], [
      ['Kashmere Gate', 'Delhi, India', 0],
      ['Chandni Chowk', 'Delhi, India', 5],
      ['New Delhi', 'Delhi, India', 0],
      ['Rajiv Chowk', 'Delhi, India', 10],
      ['Central Secretariat', 'Delhi, India', 5],
      ['INA', 'Delhi, India', 0],
      ['AIIMS', 'Delhi, India', 5],
      ['Green Park', 'Delhi, India', 0],
      ['Hauz Khas', 'Delhi, India', 10],
      ['Malviya Nagar', 'Delhi, India', 5],
    ]),
  }),
  makePlaylist({
    id: 'morning',
    name: 'Morning Walk',
    icon: 'run',
    tone: 'pink',
    createdLabel: '3 Sep 2026',
    lastUsedHours: 168,
    lastUsedLabel: '1 week ago',
    lastUsedExact: '17 Oct 2026, 7:05 AM',
    timesUsed: 2,
    distanceKm: 12.6,
    estimatedMinutes: 45,
    collectionId: 'weekend',
    mockMinutes: 90,
    avgSessionMinutes: 45,
    mockDelta: -3,
    avgDelta: -2,
    stops: ringStops('morning', [28.593, 77.221], [
      ['Lodhi Garden Gate', 'New Delhi, Delhi, India', 0],
      ['Bada Gumbad', 'New Delhi, Delhi, India', 5],
      ['Athpula Bridge', 'New Delhi, Delhi, India', 5],
      ['Deer Park', 'New Delhi, Delhi, India', 10],
      ['India Habitat Centre', 'New Delhi, Delhi, India', 0],
    ]),
  }),
  makePlaylist({
    id: 'cafe',
    name: 'Cafe Hopping',
    icon: 'utensils',
    tone: 'purple',
    favourite: true,
    createdLabel: '21 Aug 2026',
    lastUsedHours: 192,
    lastUsedLabel: '1 week ago',
    lastUsedExact: '16 Oct 2026, 4:45 PM',
    timesUsed: 6,
    distanceKm: 22.8,
    estimatedMinutes: 100,
    collectionId: 'food',
    mockMinutes: 600,
    avgSessionMinutes: 100,
    mockDelta: 9,
    avgDelta: 5,
    stops: ringStops('cafe', [28.55, 77.2], [
      ['Khan Market', 'New Delhi, Delhi, India', 0],
      ['Cafe Lota', 'New Delhi, Delhi, India', 20],
      ['Blue Tokai', 'New Delhi, Delhi, India', 15],
      ['Diggin', 'New Delhi, Delhi, India', 15],
      ['Hauz Khas Social', 'New Delhi, Delhi, India', 25],
      ['The Grammar Room', 'New Delhi, Delhi, India', 10],
      ['Kunzum Travel', 'New Delhi, Delhi, India', 10],
      ['Saket Select', 'New Delhi, Delhi, India', 15],
    ]),
  }),
  makePlaylist({
    id: 'weekend',
    name: 'Weekend Drive',
    icon: 'car',
    tone: 'blue',
    createdLabel: '11 Apr 2026',
    lastUsedHours: 336,
    lastUsedLabel: '2 weeks ago',
    lastUsedExact: '10 Oct 2026, 11:20 AM',
    timesUsed: 1,
    distanceKm: 78.3,
    estimatedMinutes: 130,
    collectionId: 'weekend',
    mockMinutes: 130,
    avgSessionMinutes: 130,
    mockDelta: 2,
    avgDelta: 1,
    stops: ringStops('weekend', [28.58, 77.25], [
      ['India Gate', 'New Delhi, Delhi, India', 0],
      ['Purana Qila', 'New Delhi, Delhi, India', 10],
      ["Humayun's Tomb", 'New Delhi, Delhi, India', 15],
      ['Akshardham', 'New Delhi, Delhi, India', 20],
      ['Mayur Vihar', 'Delhi, India', 5],
      ['Noida Sector 18', 'Noida, Uttar Pradesh, India', 10],
      ['Botanical Garden', 'Noida, Uttar Pradesh, India', 10],
      ['Kalindi Kunj', 'Delhi, India', 5],
      ['Tughlakabad', 'Delhi, India', 10],
      ['Qutub Minar', 'New Delhi, Delhi, India', 15],
      ['Chhatarpur', 'Delhi, India', 5],
      ['Cyber Hub', 'Gurugram, Haryana, India', 20],
    ]),
  }),
  makePlaylist({
    id: 'work',
    name: 'Work Route',
    icon: 'briefcase',
    tone: 'green',
    createdLabel: '1 Mar 2026',
    lastUsedHours: 360,
    lastUsedLabel: '2 weeks ago',
    lastUsedExact: '9 Oct 2026, 8:50 AM',
    timesUsed: 4,
    distanceKm: 18.4,
    estimatedMinutes: 55,
    collectionId: 'commute',
    mockMinutes: 220,
    avgSessionMinutes: 55,
    mockDelta: 5,
    avgDelta: 2,
    stops: ringStops('work', [28.495, 77.088], [
      ['Dwarka Sector 21', 'Delhi, India', 0],
      ['IGI Airport', 'Delhi, India', 10],
      ['Aerocity', 'Delhi, India', 5],
      ['Dhaula Kuan', 'Delhi, India', 0],
      ['Connaught Place', 'New Delhi, Delhi, India', 5],
      ['India Gate', 'New Delhi, Delhi, India', 0],
    ]),
  }),
  makePlaylist({
    id: 'food-walk',
    name: 'Old Delhi Food Walk',
    icon: 'utensils',
    tone: 'pink',
    favourite: true,
    createdLabel: '14 Sep 2026',
    lastUsedHours: 96,
    lastUsedLabel: '4 days ago',
    lastUsedExact: '20 Oct 2026, 8:30 PM',
    timesUsed: 7,
    distanceKm: 14.2,
    estimatedMinutes: 90,
    collectionId: 'food',
    mockMinutes: 630,
    avgSessionMinutes: 90,
    mockDelta: 11,
    avgDelta: 6,
    stops: ringStops('food-walk', [28.655, 77.23], [
      ["Karim's", 'Old Delhi, Delhi, India', 25],
      ['Al Jawahar', 'Old Delhi, Delhi, India', 20],
      ['Paranthe Wali Gali', 'Old Delhi, Delhi, India', 15],
      ['Shyam Sweets', 'Old Delhi, Delhi, India', 10],
      ['Kuremal Kulfi', 'Old Delhi, Delhi, India', 10],
      ['Natraj Dahi Balle', 'Old Delhi, Delhi, India', 10],
      ['Town Hall', 'Old Delhi, Delhi, India', 5],
      ['Chawri Bazar', 'Old Delhi, Delhi, India', 5],
      ['Fatehpuri', 'Old Delhi, Delhi, India', 0],
    ]),
  }),
  makePlaylist({
    id: 'airport',
    name: 'Airport Transfer',
    icon: 'plane',
    tone: 'blue',
    createdLabel: '8 Jan 2026',
    lastUsedHours: 6,
    lastUsedLabel: '6 hours ago',
    lastUsedExact: '24 Oct 2026, 1:40 PM',
    timesUsed: 9,
    distanceKm: 31.8,
    estimatedMinutes: 50,
    collectionId: 'airport',
    mockMinutes: 450,
    avgSessionMinutes: 50,
    mockDelta: 15,
    avgDelta: 4,
    stops: ringStops('airport', [28.556, 77.085], [
      ['IGI Terminal 3', 'Delhi, India', 0],
      ['Aerocity', 'Delhi, India', 10],
      ['Aerocity Metro', 'Delhi, India', 5],
      ['Mahipalpur', 'Delhi, India', 0],
    ]),
  }),
  makePlaylist({
    id: 'south',
    name: 'South Delhi Loop',
    icon: 'trees',
    tone: 'green',
    createdLabel: '27 Aug 2026',
    lastUsedHours: 240,
    lastUsedLabel: '10 days ago',
    lastUsedExact: '14 Oct 2026, 3:15 PM',
    timesUsed: 3,
    distanceKm: 24.6,
    estimatedMinutes: 68,
    collectionId: 'city',
    mockMinutes: 204,
    avgSessionMinutes: 68,
    mockDelta: 0,
    avgDelta: 0,
    stops: ringStops('south', [28.53, 77.22], [
      ['Greater Kailash', 'New Delhi, Delhi, India', 0],
      ['Nehru Place', 'New Delhi, Delhi, India', 10],
      ['Lotus Temple', 'New Delhi, Delhi, India', 15],
      ['Kalkaji', 'New Delhi, Delhi, India', 5],
      ['Saket', 'New Delhi, Delhi, India', 10],
      ['Select Citywalk', 'New Delhi, Delhi, India', 20],
      ['Chhatarpur', 'Delhi, India', 5],
    ]),
  }),
  makePlaylist({
    id: 'night',
    name: 'Night Markets',
    icon: 'moon',
    tone: 'purple',
    createdLabel: '5 Oct 2026',
    lastUsedHours: 480,
    lastUsedLabel: '3 weeks ago',
    lastUsedExact: '3 Oct 2026, 9:10 PM',
    timesUsed: 2,
    distanceKm: 19.7,
    estimatedMinutes: 85,
    collectionId: 'food',
    mockMinutes: 170,
    avgSessionMinutes: 85,
    mockDelta: 7,
    avgDelta: 3,
    stops: ringStops('night', [28.63, 77.22], [
      ['Janpath', 'New Delhi, Delhi, India', 10],
      ['Connaught Place', 'New Delhi, Delhi, India', 15],
      ['Palika Bazaar', 'New Delhi, Delhi, India', 10],
      ['Sarojini Nagar', 'New Delhi, Delhi, India', 20],
      ['Dilli Haat', 'New Delhi, Delhi, India', 15],
      ['Majnu ka Tilla', 'Delhi, India', 10],
    ]),
  }),
];

export function isMostUsed(playlist: Playlist): boolean {
  return playlist.timesUsed >= MOST_USED_AT;
}

export function formatDistance(km: number): string {
  return `${km.toFixed(1)} km`;
}

export function formatDuration(minutes: number): string {
  const whole = Math.max(0, Math.round(minutes));
  const hours = Math.floor(whole / 60);
  const mins = whole % 60;
  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
}

export interface WaitBand {
  id: string;
  label: string;
  color: string;
  count: number;
  percent: number;
}

const WAIT_DEFS: Array<{ id: string; label: string; color: string; test: (minutes: number) => boolean }> = [
  { id: 'under5', label: '< 5 min', color: '#7c5cff', test: (minutes) => minutes < 5 },
  { id: 'to10', label: '5–10 min', color: '#c4b5fd', test: (minutes) => minutes >= 5 && minutes <= 10 },
  { id: 'to15', label: '10–15 min', color: '#f0abfc', test: (minutes) => minutes > 10 && minutes <= 15 },
  { id: 'over15', label: '> 15 min', color: '#f5d0fe', test: (minutes) => minutes > 15 },
];

export function waitBands(stops: PlaylistStop[]): WaitBand[] {
  const total = stops.length;
  return WAIT_DEFS.map((band) => {
    const count = stops.filter((stop) => band.test(stop.waitMinutes)).length;
    return {
      id: band.id,
      label: band.label,
      color: band.color,
      count,
      percent: total === 0 ? 0 : Math.round((count / total) * 100),
    };
  });
}

export interface RouteLeg {
  from: string;
  to: string;
  km: number;
}

export function legDistances(playlist: Playlist): RouteLeg[] {
  const { stops } = playlist;
  if (stops.length < 2) return [];
  const weights = stops.slice(0, -1).map((_, index) => 0.75 + ((index * 3) % 5) * 0.1);
  const weightSum = weights.reduce((sum, value) => sum + value, 0);
  const rounded = weights.map((weight) => Math.round(((playlist.distanceKm * weight) / weightSum) * 10) / 10);
  const drift = Math.round((playlist.distanceKm - rounded.reduce((sum, value) => sum + value, 0)) * 10) / 10;
  rounded[rounded.length - 1] = Math.round((rounded[rounded.length - 1] + drift) * 10) / 10;
  return stops.slice(0, -1).map((stop, index) => ({
    from: stop.name,
    to: stops[index + 1].name,
    km: rounded[index],
  }));
}

function formatCreated(date: Date): string {
  return `${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
}

export function createEmptyPlaylist(name: string, collectionId: string): Playlist {
  const now = new Date();
  const id = nextId('pl');
  return {
    id,
    name,
    icon: 'list',
    tone: 'purple',
    favourite: false,
    active: false,
    createdLabel: formatCreated(now),
    lastUsedHours: 0,
    lastUsedLabel: 'Just now',
    lastUsedExact: formatCreated(now),
    timesUsed: 0,
    distanceKm: 0,
    estimatedMinutes: 0,
    collectionId,
    mockMinutes: 0,
    avgSessionMinutes: 0,
    mockDelta: 0,
    avgDelta: 0,
    usage: Array.from({ length: 31 }, () => 0),
    activity: [{ id: `${id}-created`, title: 'Playlist created', time: 'Just now' }],
    stops: [],
  };
}

export function createStop(playlistId: string, input: {
  name: string;
  area: string;
  waitMinutes: number;
  after?: PlaylistStop;
}): PlaylistStop {
  const indexSeed = input.after ? 1 : 0;
  return {
    id: nextId(`${playlistId}-stop`),
    name: input.name,
    area: input.area,
    waitMinutes: input.waitMinutes,
    latitude: Number(((input.after?.latitude ?? 28.6129) + 0.008).toFixed(4)),
    longitude: Number(((input.after?.longitude ?? 77.2295) + 0.008 * (indexSeed + 1)).toFixed(4)),
    color: STOP_CYCLE[(input.after ? 2 : 0) % STOP_CYCLE.length],
  };
}

function csvEscape(value: string): string {
  if (/[",\n]/.test(value)) return `"${value.replace(/"/g, '""')}"`;
  return value;
}

function splitCsv(line: string): string[] {
  const cells: string[] = [];
  let current = '';
  let quoted = false;
  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    if (char === '"') {
      if (quoted && line[index + 1] === '"') {
        current += '"';
        index += 1;
      } else {
        quoted = !quoted;
      }
    } else if (char === ',' && !quoted) {
      cells.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  cells.push(current.trim());
  return cells;
}

export function playlistsToCsv(list: Playlist[]): string {
  const header = 'name,stops,distanceKm,minutes,timesUsed,favourite,collection';
  const rows = list.map((playlist) => [
    csvEscape(playlist.name),
    String(playlist.stops.length),
    String(playlist.distanceKm),
    String(playlist.estimatedMinutes),
    String(playlist.timesUsed),
    playlist.favourite ? 'yes' : 'no',
    playlist.collectionId,
  ].join(','));
  return [header, ...rows].join('\n');
}

function nameFromFile(fileName: string): string {
  const base = fileName.replace(/\.csv$/i, '').replace(/[-_]+/g, ' ').trim();
  if (!base) return 'Imported playlist';
  return base.charAt(0).toUpperCase() + base.slice(1);
}

function knownCollection(id: string): string {
  return PLAYLIST_COLLECTIONS.some((collection) => collection.id === id) ? id : 'city';
}

export function parsePlaylistImport(text: string, fileName: string): Playlist[] {
  const lines = text.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  if (lines.length < 2) return [];
  const header = splitCsv(lines[0]).map((cell) => cell.toLowerCase());

  if (header.includes('distancekm')) {
    return lines.slice(1).map((line, index) => {
      const cells = splitCsv(line);
      const name = cells[0];
      if (!name) return null;
      const stopCount = Math.max(0, Math.min(24, Number(cells[1]) || 0));
      const rows: StopRow[] = Array.from({ length: stopCount }, (_, stopIndex) => [
        `Stop ${stopIndex + 1}`,
        'Imported stop',
        10,
      ]);
      const playlist = createEmptyPlaylist(name, knownCollection(cells[6] || 'city'));
      return {
        ...playlist,
        id: `${playlist.id}-${index}`,
        favourite: (cells[5] || '').toLowerCase() === 'yes',
        distanceKm: Number(cells[2]) || 0,
        estimatedMinutes: Number(cells[3]) || 0,
        timesUsed: Number(cells[4]) || 0,
        stops: ringStops(playlist.id, [28.61, 77.23], rows),
      };
    }).filter((playlist): playlist is Playlist => playlist != null);
  }

  if (!header.includes('latitude') || !header.includes('stop')) return [];

  const stops: PlaylistStop[] = [];
  lines.slice(1).forEach((line, index) => {
    const cells = splitCsv(line);
    const name = cells[0];
    const latitude = Number(cells[3]);
    const longitude = Number(cells[4]);
    if (!name || Number.isNaN(latitude) || Number.isNaN(longitude)) return;
    const wait = Number(cells[2]);
    stops.push({
      id: nextId('import-stop'),
      name,
      area: cells[1] || 'New Delhi, Delhi, India',
      waitMinutes: Number.isNaN(wait) ? 0 : wait,
      latitude,
      longitude,
      color: STOP_CYCLE[index % STOP_CYCLE.length],
    });
  });
  if (stops.length === 0) return [];
  const playlist = createEmptyPlaylist(nameFromFile(fileName), 'city');
  return [{
    ...playlist,
    stops,
    distanceKm: Number((Math.max(stops.length - 1, 0) * 3.2).toFixed(1)),
    estimatedMinutes: stops.length * 8,
  }];
}
